import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate } from "react-router";
import { API_URL } from "../../common/AppConstants";
import { checkQRQuickOrderAvailability } from "../../api/Product";

type Selection = "now" | "later";

const QuickOrder: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [productDetail, setProductDetail] = useState<any | null>(null);
  const [selection, setSelection] = useState<Selection>("now");
  const [redirectNow, setRedirectNow] = useState("/rental");
  const [redirectLater, setRedirectLater] = useState("/");

  const [imageLoadError, setImageLoadError] = useState(false);
  const [timeSlotAvailable, setTimeSlotAvailable] = useState<boolean>(true);
  const [timeSlotMessage, setTimeSlotMessage] = useState<string>("");
  const [checkingTimeSlot, setCheckingTimeSlot] = useState<boolean>(false);
  const [availabilityData, setAvailabilityData] = useState<any>(null);
  const [nextAvailable, setNextAvailable] = useState<any>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");  

    if (!code) {
      setError("Invalid or missing code");
      setLoading(false);
      return;
    }
    setToken(code);

    const checkProductAvailability = async () => {
      try {
        // Use new unified API endpoint
        const now = new Date();
        
        // Get local date (not UTC) to avoid timezone issues
        const year = now.getFullYear();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const currentDate = `${year}-${month}-${day}`;
        
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const currentTime = `${hours}:${minutes}`;

        console.log('🔍 QR Quick Order API Request:', {
          qr_hash: code,
          date: currentDate,
          time: currentTime,
          localDateTime: now.toLocaleString()
        });

        checkQRQuickOrderAvailability({
          qr_hash: code,
          date: currentDate,
          time: currentTime
        }, (jsonRes: any, status: any, error: any) => {
          console.log('📡 QR Quick Order API Response:', { status, jsonRes, error });
          
          if (status === 200) {
            // Check if API returned success or error
            if (jsonRes?.success) {
              // Successful response with availability data
              const productData = jsonRes.product;
              setProductDetail(productData);
              setAvailabilityData(jsonRes);
              
              console.log('✅ Product loaded:', productData?.family?.display_name);
              console.log('📊 Availability:', jsonRes.availability);
              console.log('⏰ Available Durations:', jsonRes.available_durations);
              
              // Check if current time slot is available
              if (jsonRes.availability && !productData?.is_reserved) {
                const isAvailable = jsonRes.availability.is_available;
                setTimeSlotAvailable(isAvailable);
                
                console.log(`🎯 Time slot ${jsonRes.availability.requested_time_12h}: ${isAvailable ? '✅ Available' : '❌ Unavailable'}`);
                
                if (!isAvailable) {
                  // Check if there's a next available time slot
                  if (jsonRes.next_available) {
                    console.log('⏰ Next available:', jsonRes.next_available);
                    setNextAvailable(jsonRes.next_available);
                    setTimeSlotMessage(
                      jsonRes.next_available.message || `Item will be available at ${jsonRes.next_available.time_12h}. Click "Order Now" to reserve it for that time.`
                    );
                  } else {
                    setSelection("later");
                    setTimeSlotMessage(
                      `The requested time slot (${jsonRes.availability.requested_time_12h}) is currently unavailable. Please use "Order Later" to schedule your reservation for another time.`
                    );
                  }
                } else {
                  setTimeSlotMessage("");
                  setNextAvailable(null);
                }
              }
            } else if (jsonRes?.error) {
              // API returned error (e.g., "Requested time is in the past")
              console.warn('⚠️ API returned error:', jsonRes.error);
              
              // Still load product details if available
              if (jsonRes?.product) {
                setProductDetail(jsonRes.product);
                console.log('✅ Product loaded:', jsonRes.product?.family?.display_name);
              }
              
              // Handle specific error cases
              if (jsonRes.error.includes('past') || jsonRes.error.includes('business hours')) {
                // Time is in the past or outside business hours
                setTimeSlotAvailable(false);
                setSelection("later");
                setTimeSlotMessage(
                  `Current time is not available for immediate booking. Please use "Order Later" to schedule your reservation.`
                );
                console.log('🕐 Current time unavailable - defaulting to Order Later');
              } else {
                // Other errors - allow Order Later but show message
                setTimeSlotAvailable(false);
                setSelection("later");
                setTimeSlotMessage(jsonRes.error);
              }
            }
          } else {
            console.error('❌ API Error:', error);
            setError(error || "Failed to verify link");
          }
          setLoading(false);
        });
      } catch (err: any) {
        setError(err.message || "Something went wrong");
        setLoading(false);
      }
    };


    checkProductAvailability();
  }, []);

  const isContinueDisabled = () => {
    if (checkingTimeSlot) return true;
    // Disable if Order Now is selected but item is not available (no immediate availability and no next_available)
    if (selection === "now" && !timeSlotAvailable && !nextAvailable) return true;
    // Also disable if Order Now is selected but unavailable (even if nextAvailable exists, user must confirm wait)
    // This forces user to either confirm the wait dialog or switch to Order Later
    return false;
  };

  const handleContinue = () => {
    // Get store_url from API response (added by backend)
    const storeUrl = productDetail?.store_url
    const isLocal = false

    const protocol = isLocal ? "http" : "https"
    const domain = isLocal ? `${storeUrl}:3000` : storeUrl
    let destination = ""
    
    if (selection === "now") {
      const now = new Date();
      const year = now.getFullYear();
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const day = now.getDate().toString().padStart(2, '0');
      const currentDate = `${year}-${month}-${day}`;

      // Check if item is available now or has next_available time
      if (timeSlotAvailable) {
        // Use rounded_time from API response (next 30-min slot), fallback to current time
        const roundedTime = availabilityData?.availability?.rounded_time
          ? availabilityData.availability.rounded_time.substring(0, 5) // "09:30:00" -> "09:30"
          : `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        destination = `${protocol}://${domain}/rental?q=1&p=${token}&date=${currentDate}&time=${roundedTime}`;
      } else if (nextAvailable) {
        // Show wait confirmation with API's dynamic message
        const confirmWait = window.confirm(nextAvailable.message);
        
        if (confirmWait) {
          // User agrees to wait - redirect with next available time
          const nextTime = nextAvailable.time.substring(0, 5); // "14:30:00" -> "14:30"
          const now = new Date();
          const year = now.getFullYear();
          const month = (now.getMonth() + 1).toString().padStart(2, '0');
          const day = now.getDate().toString().padStart(2, '0');
          const date = `${year}-${month}-${day}`;
          
          console.log(`✅ User confirmed wait. Redirecting with time: ${nextTime}`);
          destination = `${protocol}://${domain}/rental?q=1&p=${token}&date=${date}&time=${nextTime}`;
        } else {
          console.log('❌ User declined wait option');
          return; // Don't redirect if user cancels
        }
      } else {
        // Should not reach here, but fallback to Order Later
        destination = `${protocol}://${domain}/rental?r=1&p=${token}`;
      }
    } else if (selection === "later") {
      destination = `${protocol}://${domain}/rental?r=1&p=${token}`;
    }

    if (destination) {
      window.location.href = destination;
    }
  };

  if (loading && !productDetail) {
    return (
      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error && !productDetail) {
    return (
      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#191919",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        width: "100%",
        p: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: "960px",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
        }}
      >
        <Box sx={{ width: "100%", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "white",
              padding: "16px 9px",
              borderRadius: "9px 9px 0 0",
              backgroundColor: productDetail?.storeContent?.primary_color || "#1976d2",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "white", fontSize: "20px" }}
              >
                {productDetail?.store_name || "Order decision"}
              </Typography>
              <Typography variant="body2" sx={{ color: "white", opacity: 0.8 }}>
                Choose whether to proceed now or schedule for later.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "#fff",
              p: 3,
            }}
          >
            {error && productDetail && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            {productDetail?.is_reserved ? (
              <Box sx={{ mb: 3 }}>
                <Alert 
                  severity="warning" 
                  icon={<ErrorOutlineIcon />}
                  sx={{ 
                    mb: 3, 
                    borderRadius: "8px",
                    "& .MuiAlert-message": { width: "100%" }
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    Currently Booked
                  </Typography>
                  <Typography variant="body2">
                    This specific vehicle is currently reserved. Please choose from our available recommendations below.
                  </Typography>
                </Alert>

                {productDetail?.recommendations && productDetail.recommendations.length > 0 ? (
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ mb: 2, fontWeight: 700, color: "#333" }}
                    >
                      Recommended Alternatives
                    </Typography>
                    <Grid container spacing={2}>
                      {productDetail.recommendations.map((rec: any) => (
                        <Grid item xs={12} sm={6} key={rec.id}>
                          <Card 
                            variant="outlined" 
                            sx={{ 
                              borderRadius: "8px", 
                              borderColor: "#e0e0e0",
                              transition: "all 0.25s ease",
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                              "&:hover": { 
                                borderColor: productDetail?.storeContent?.primary_color || "#1976d2", 
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)"
                              }
                            }}
                          >
                            <CardActionArea 
                              sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                              onClick={() => {
                                const storeUrl = rec.current_location_tbl?.brandStores?.store_url;
                                const qrHash = rec.qr_hash;
                                
                                if (storeUrl) {
                                  // Ensure the URL has a protocol
                                  const protocol = storeUrl.includes("localhost") ? "http" : "https";
                                  // window.location.href = `${protocol}://${storeUrl}/quick-order?code=${qrHash}`;
                                  window.location.href = `${protocol}://${storeUrl}/rental`;
                                } else {
                                  // Fallback to local update if storeUrl is missing
                                  setProductDetail({
                                    ...productDetail,
                                    ...rec,
                                    is_reserved: false,
                                    recommendations: productDetail.recommendations.filter((r: any) => r.id !== rec.id)
                                  });
                                }
                              }}
                            >
                              {/* Recommendation Image */}
                              <Box
                                sx={{
                                  height: "140px",
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                  borderBottom: "1px solid #eee"
                                }}
                              >
                                {rec.family?.img_url ? (
                                  <img
                                    src={API_URL + rec.family.img_url}
                                    alt={rec.family?.display_name || "Product"}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <DirectionsCarIcon sx={{ fontSize: 40, color: "#ccc" }} />
                                )}
                              </Box>

                              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                                  {rec.family?.display_name || rec.family?.family}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                                  {rec.line?.line}
                                </Typography>
                                
                                <Divider sx={{ my: 1, opacity: 0.6 }} />
                                
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 0.2 }}>
                                    Available at:
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600} color="primary">
                                    {rec.current_location_tbl?.brandStores?.store_name || "Partner Store"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    {rec.current_location_tbl?.locations?.location}
                                  </Typography>
                                </Box>

                                <Button 
                                  variant="contained" 
                                  size="small"
                                  fullWidth
                                  sx={{ 
                                    mt: "auto",
                                    textTransform: "none",
                                    backgroundColor: productDetail?.storeContent?.primary_color || "#1976d2"
                                  }}
                                >
                                  Select vehicle
                                </Button>
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ color: "#666", textAlign: "center", py: 4 }}>
                    No other vehicles currently available.
                 </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                {!timeSlotAvailable && timeSlotMessage && (
                  <Alert 
                    severity="warning" 
                    icon={<ErrorOutlineIcon />}
                    sx={{ mb: 2, borderRadius: "8px" }}
                  >
                    <Typography variant="body2">
                      {timeSlotMessage}
                    </Typography>
                  </Alert>
                )}
                
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 700, color: "#333" }}
                >
                  How would you like to place your order?
                </Typography>

                <Grid container spacing={2}>
                  {[
                    {
                      value: "now" as Selection,
                      title: "Order now",
                      description: "Start your order immediately with the current details.",
                    },
                    {
                      value: "later" as Selection,
                      title: "Order later",
                      description:
                        "Save this link and schedule your order for another time.",
                    },
                  ].map((opt) => {
                    const isSelected = selection === opt.value;
                    const isDisabled = opt.value === "now" && !timeSlotAvailable;
                    return (
                      <Grid item xs={12} sm={6} key={opt.value}>
                        <Card
                          onClick={() => !isDisabled && setSelection(opt.value)}
                          sx={{
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            borderRadius: 1,
                            border: isSelected ? "2px solid" : "1px solid",
                            borderColor: isSelected ? "#1976d2" : "divider",
                            backgroundColor: isDisabled 
                              ? "rgba(0,0,0,0.05)"
                              : isSelected
                              ? "rgba(25,118,210,0.08)"
                              : "background.paper",
                            transition: "all 0.25s ease",
                            "&:hover": { borderColor: isDisabled ? "divider" : "#1976d2" },
                            height: "100%",
                            opacity: isDisabled ? 0.6 : 1,
                          }}
                        >
                          <CardActionArea sx={{ p: 2, textAlign: "left" }} disabled={isDisabled}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              color="text.primary"
                            >
                              {opt.title}
                              {isDisabled && (
                                <Typography
                                  component="span"
                                  sx={{ ml: 1, fontSize: "12px", color: "error.main", fontWeight: 500 }}
                                >
                                  (Unavailable)
                                </Typography>
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, color: "text.secondary" }}
                            >
                              {opt.description}
                            </Typography>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, backgroundColor: productDetail?.storeContent?.primary_color || "#1976d2" }}
                  fullWidth
                  onClick={handleContinue}
                  disabled={isContinueDisabled()}
                >
                  {checkingTimeSlot ? "Checking availability..." : "Continue"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Product Detail Sidebar inspired by SummaryCard */}
        <Box
          sx={{
            width: "100%",
            alignSelf: "flex-start",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            overflow: "hidden",
            backgroundColor: "#e6edf9",
            maxWidth: { xs: "100%", md: "320px" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Image Section */}
          <Box
            sx={{
              position: "relative",
              height: "200px",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {productDetail?.family?.img_url && !imageLoadError ? (
              <img
                src={API_URL + productDetail.family.img_url}
                alt={productDetail?.family?.display_name || "Product"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  backgroundColor: "white",
                }}
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <Box sx={{ color: "#666", textAlign: "center", p: 2 }}>
                <Typography variant="caption">No Image Available</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ p: 1.5 }}>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#333333",
              }}
            >
              {productDetail?.family?.display_name || "Vehicle"}
            </Typography>

            {productDetail?.family?.summary && (
              <Box sx={{ p: 1,  }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    lineHeight: 1.4,
                    fontSize: "13px",
                    "& p": {
                      margin: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: productDetail.family.summary }}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: "#ccc", borderWidth: 1 }} />

          <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#1f2937",
                mb: 1.5,
              }}
            >
              {productDetail?.family?.display_name || "Vehicle"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DirectionsCarIcon sx={{ fontSize: 18, color: "#666" }} />
                <Typography sx={{ fontSize: 14, color: "#1f2937", fontWeight: 600 }}>
                  {productDetail?.category?.category || "N/A"}
                </Typography>
              </Box>

              {productDetail?.line?.line && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, color: "#6b7280", ml: 3.2 }}>
                    {productDetail.line.line} ({productDetail.line.size})
                  </Typography>
                </Box>
              )}

              {productDetail?.current_location_tbl?.locations?.location && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, color: "#666", mt: "2px" }} />
                  <Box>
                    <Typography sx={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>
                      Current Location
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="primary">
                      {productDetail.current_location_tbl?.brandStores?.store_name || "Partner Store"}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#1f2937", fontWeight: 600 }}>
                      {productDetail.current_location_tbl.locations.location}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: "auto", p: 2, textAlign: "center" }}>
             {/* Logo or Branding could go here if available */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default QuickOrder;

